import { Link } from "wouter";
import { Phone, Mail, MapPin } from "lucide-react";
import logo from "@assets/graystone-logo.gif";
import { GOOGLE_REVIEWS_URL, YELP_URL } from "@/data/reviewLinks";

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-secondary text-secondary-foreground pt-16 pb-24 lg:pb-8 border-t border-secondary-border">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          
          <div className="space-y-6">
            <Link href="/">
              <img src={logo} alt="Graystone Roofing & Siding" width={323} height={134} className="h-12 w-auto bg-white p-2 rounded" />
            </Link>
            <p className="text-secondary-foreground/80 leading-relaxed text-sm">
              Premium Roofing & Siding for Lancaster County Homes. Locally owned and operated, serving homeowners with quality products installed properly since 2007.
            </p>
            <div className="space-y-3">
              <a href="tel:+17177257170" className="flex items-center gap-3 text-secondary-foreground/90 hover:text-white transition-colors">
                <Phone className="h-5 w-5 text-primary" />
                <span className="font-semibold">717-725-7170</span>
              </a>
              <div className="flex items-start gap-3 text-secondary-foreground/90">
                <MapPin className="h-5 w-5 text-primary shrink-0 mt-1" />
                <span>2579 Graystone Rd<br/>East Petersburg, PA 17520</span>
              </div>
              <a href="mailto:info@lancasterparoofing.com" className="flex items-center gap-3 text-secondary-foreground/90 hover:text-white transition-colors">
                <Mail className="h-5 w-5 text-primary" />
                <span>info@lancasterparoofing.com</span>
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-white font-serif font-bold text-xl mb-6 border-b border-secondary-foreground/20 pb-2">Services</h3>
            <ul className="space-y-3 text-sm">
              <li><Link href="/services/roofing-company-lancaster-pa/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Roofing Installation</Link></li>
              <li><Link href="/services/roof-repair-lancaster-pa/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Roof Repair</Link></li>
              <li><Link href="/services/siding-contractor-lancaster-pa/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Vinyl Siding</Link></li>
              <li><Link href="/services/gutter-installation-lancaster-pa-york-pa/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Gutter Installation</Link></li>
              <li><Link href="/services/roofing-inspection-lancaster-pa/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Roof Inspections</Link></li>
              <li><Link href="/services/roofing-storm-damage-lancaster-pa/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Storm Damage</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-serif font-bold text-xl mb-6 border-b border-secondary-foreground/20 pb-2">Company</h3>
            <ul className="space-y-3 text-sm">
              <li><Link href="/about-graystone-roofing-siding-lancaster/" className="text-secondary-foreground/80 hover:text-primary transition-colors">About Us</Link></li>
              <li><Link href="/roofing-siding-york-lancaster-pa-testimonials/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Testimonials</Link></li>
              <li><Link href="/roofing-siding-photos-lancaster-pa/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Project Gallery</Link></li>
              <li><Link href="/service-areas/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Service Areas</Link></li>
              <li><Link href="/blog/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Help Hub</Link></li>
              <li><Link href="/roofing-siding-cost-calculator-lancaster-pa/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Cost Calculator</Link></li>
              <li><Link href="/contact-graystone-roofing-siding/" className="text-secondary-foreground/80 hover:text-primary transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-serif font-bold text-xl mb-6 border-b border-secondary-foreground/20 pb-2">Trust & Quality</h3>
            <div className="space-y-4">
              <div className="bg-secondary-foreground/10 p-4 rounded border border-secondary-foreground/20">
                <div className="font-bold text-white mb-1">A+ Rating</div>
                <div className="text-sm text-secondary-foreground/80">Better Business Bureau</div>
              </div>
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-secondary-foreground/10 p-4 rounded border border-secondary-foreground/20 transition-colors hover:border-primary"
                data-testid="link-google-reviews"
              >
                <div className="font-bold text-white mb-1">5.0 Stars</div>
                <div className="text-sm text-secondary-foreground/80">Google Reviews</div>
              </a>
              <a
                href={YELP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-secondary-foreground/10 p-4 rounded border border-secondary-foreground/20 transition-colors hover:border-primary"
                data-testid="link-yelp-reviews"
              >
                <div className="font-bold text-white mb-1">5.0 Stars</div>
                <div className="text-sm text-secondary-foreground/80">Yelp Reviews</div>
              </a>
              <div className="bg-secondary-foreground/10 p-4 rounded border border-secondary-foreground/20">
                <div className="font-bold text-white mb-1">GAF Certified</div>
                <div className="text-sm text-secondary-foreground/80">Home Remodeling Contractor</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-secondary-foreground/20 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-secondary-foreground/60">
          <p>&copy; {currentYear} Graystone Roofing & Siding. All rights reserved.</p>
          <div className="flex gap-4">
            <span>Locally owned and operated in East Petersburg, PA</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
